import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { Download, Image as ImageIcon, Loader2, Camera, Video } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { GalleryVideoBlock } from '../components/GalleryVideoBlock';
import { GalleryMasonry } from '../components/GalleryMasonry';
import { GalleryPhotoCard } from '../components/GalleryPhotoCard';
import { GalleryPhotoLightbox } from '../components/GalleryPhotoLightbox';
import { galleryAPI } from '../api/apiAdapter';

const FOLDER_PHOTOS = 'wedding_day_all_photos';
/** Сколько фото монтировать в DOM за раз */
const PHOTO_MOUNT_BATCH = 24;
/** За сколько строк до конца текущей порции начинать монтировать следующую */
const MOUNT_AHEAD_ROWS = 5;
/** На сколько строк вперёд предзагружать превью (даже до монтирования в DOM) */
const PRELOAD_AHEAD_ROWS = 8;
/** За сколько вьюпортов до триггер-карточки начинать подгрузку */
const PREFETCH_ROOT_MARGIN = '0px 0px 300% 0px';
const MASONRY_COLUMNS = { 350: 3, 750: 2, 900: 3, 1200: 4 };
const MASONRY_GUTTER = '8px';

function getColumnCount(width: number): number {
  const breakpoints = Object.entries(MASONRY_COLUMNS)
    .map(([bp, cols]) => [Number(bp), cols] as const)
    .sort((a, b) => a[0] - b[0]);
  let count = breakpoints[0]?.[1] ?? 2;
  for (const [bp, cols] of breakpoints) {
    if (width >= bp) count = cols;
  }
  return count;
}

function useColumnCount(): number {
  const [count, setCount] = useState(() =>
    typeof window !== 'undefined' ? getColumnCount(window.innerWidth) : 3,
  );
  useEffect(() => {
    const onResize = () => setCount(getColumnCount(window.innerWidth));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return count;
}

function preloadImage(src: string): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve(null);
    img.src = src;
  });
}
/** Фиксированные пути к видео в папке wedding_day_video/ */
const VIDEO_PATH_BEST_MOMENTS = 'wedding_day_video/wedding_best_moments.mp4';
const VIDEO_PATH_MAIN = 'wedding_day_video/wedding_video.mp4';

export const Gallery: React.FC = () => {
  const [photoPaths, setPhotoPaths] = useState<string[]>([]);
  const [photoUrlByPath, setPhotoUrlByPath] = useState<Record<string, string>>({});
  const [photoThumbByPath, setPhotoThumbByPath] = useState<Record<string, string>>({});
  const [photosUrlsLoading, setPhotosUrlsLoading] = useState(false);
  const [visiblePhotoCount, setVisiblePhotoCount] = useState(PHOTO_MOUNT_BATCH);
  const [loadThroughIndex, setLoadThroughIndex] = useState(-1);
  const columnCount = useColumnCount();
  const prefetchSentinelRef = useRef<HTMLDivElement | null>(null);
  const loadedThroughRef = useRef(-1);
  const mountExpandedAtRef = useRef(0);
  const photoDimensionsRef = useRef<Record<string, { width: number; height: number }>>({});
  const masonryContainerRef = useRef<HTMLDivElement>(null);
  const [masonryWidth, setMasonryWidth] = useState(0);
  const [layoutEpoch, setLayoutEpoch] = useState(0);
  const [bestMomentsStreamUrl, setBestMomentsStreamUrl] = useState<string | null>(null);
  const [bestMomentsDownloadUrl, setBestMomentsDownloadUrl] = useState<string | null>(null);
  const [bestMomentsArchiveUrl, setBestMomentsArchiveUrl] = useState<string | null>(null);
  const [videoStreamUrl, setVideoStreamUrl] = useState<string | null>(null);
  const [videoDownloadUrl, setVideoDownloadUrl] = useState<string | null>(null);
  const [videoArchiveUrl, setVideoArchiveUrl] = useState<string | null>(null);
  const [photoArchiveUrl, setPhotoArchiveUrl] = useState<string | null>(null);
  const [videoEnabled, setVideoEnabled] = useState<boolean | null>(null);
  const [photosEnabled, setPhotosEnabled] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const galleryClosed = videoEnabled === false && photosEnabled === false;
  const visiblePaths = photoPaths.slice(0, visiblePhotoCount);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setMessage('');
    (async () => {
      try {
        const status = await galleryAPI.getStatus();
        if (cancelled) return;

        setVideoEnabled(status.video_enabled);
        setPhotosEnabled(status.photos_enabled);

        if (!status.video_enabled && !status.photos_enabled) {
          setLoading(false);
          return;
        }

        setLoading(false);

        if (status.photos_enabled) {
          setPhotosUrlsLoading(true);
        }

        const videoPromise = status.video_enabled
          ? Promise.all([
              galleryAPI.getStreamUrl(VIDEO_PATH_BEST_MOMENTS).then((r) => r.url).catch(() => null),
              galleryAPI.getDownloadUrl(VIDEO_PATH_BEST_MOMENTS).then((r) => r.url).catch(() => null),
              galleryAPI.getStreamUrl(VIDEO_PATH_MAIN).then((r) => r.url).catch(() => null),
              galleryAPI.getDownloadUrl(VIDEO_PATH_MAIN).then((r) => r.url).catch(() => null),
              galleryAPI.getArchiveUrl('wedding_day_video').then((r) => r.url),
              galleryAPI.getArchiveUrl('wedding_best_moments').then((r) => r.url).catch(() => null),
            ])
          : Promise.resolve(null);

        const photosPromise = status.photos_enabled
          ? (async () => {
              try {
                const [batch, archive] = await Promise.all([
                  galleryAPI.getStreamUrlsBatch(FOLDER_PHOTOS),
                  galleryAPI.getArchiveUrl('wedding_day_all_photos').then((r) => r.url),
                ]);
                return { batch, archive };
              } finally {
                if (!cancelled) setPhotosUrlsLoading(false);
              }
            })()
          : Promise.resolve(null);

        const [videoData, photosData] = await Promise.all([videoPromise, photosPromise]);
        if (cancelled) return;

        if (videoData) {
          const [bestStream, bestDownload, mainStream, mainDownload, videoArchive, bestArchive] = videoData;
          setBestMomentsStreamUrl(bestStream);
          setBestMomentsDownloadUrl(bestDownload);
          setBestMomentsArchiveUrl(bestArchive);
          setVideoStreamUrl(mainStream);
          setVideoDownloadUrl(mainDownload);
          setVideoArchiveUrl(videoArchive);
        }

        if (photosData) {
          const { batch, archive } = photosData;
          setPhotoArchiveUrl(archive);
          const items = batch.items || [];
          if (items.length > 0 && !cancelled) {
            const map: Record<string, string> = {};
            const thumbMap: Record<string, string> = {};
            const paths: string[] = [];
            for (const item of items) {
              map[item.path] = item.url;
              if (item.thumb_url) thumbMap[item.path] = item.thumb_url;
              paths.push(item.path);
            }
            setPhotoPaths(paths);
            setPhotoUrlByPath(map);
            setPhotoThumbByPath(thumbMap);
            setVisiblePhotoCount(Math.min(PHOTO_MOUNT_BATCH, paths.length));
            loadedThroughRef.current = -1;
            mountExpandedAtRef.current = 0;
            setLoadThroughIndex(-1);
          }
        }
      } catch (e) {
        if (!cancelled) setMessage(e instanceof Error ? e.message : 'Ошибка загрузки галереи');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const getPhotoSrc = useCallback(
    (path: string) => photoThumbByPath[path] || photoUrlByPath[path],
    [photoThumbByPath, photoUrlByPath],
  );

  const expandVisiblePhotos = useCallback(() => {
    setVisiblePhotoCount((prev) => {
      if (prev >= photoPaths.length) return prev;
      return Math.min(prev + PHOTO_MOUNT_BATCH, photoPaths.length);
    });
  }, [photoPaths.length]);

  /** Построчная загрузка с опережением: слева направо, сверху вниз */
  useEffect(() => {
    if (photoPaths.length === 0) return;
    let cancelled = false;
    const cols = columnCount;
    const preloadLimit = Math.min(
      visiblePhotoCount + cols * PRELOAD_AHEAD_ROWS,
      photoPaths.length,
    );
    const firstRow = Math.floor((loadedThroughRef.current + 1) / cols);

    (async () => {
      for (let row = firstRow; row * cols < preloadLimit; row++) {
        if (cancelled) return;
        const startIdx = row * cols;
        const endIdx = Math.min(startIdx + cols - 1, preloadLimit - 1);
        loadedThroughRef.current = endIdx;
        setLoadThroughIndex(endIdx);

        const rowUrls = photoPaths
          .slice(startIdx, endIdx + 1)
          .map((path) => ({ path, url: getPhotoSrc(path) }))
          .filter((item) => Boolean(item.url)) as Array<{ path: string; url: string }>;

        const results = await Promise.all(
          rowUrls.map(async ({ path, url }) => {
            const dim = await preloadImage(url);
            if (dim) photoDimensionsRef.current[path] = dim;
          }),
        );
        void results;
        if (!cancelled) setLayoutEpoch((v) => v + 1);

        const rowsLeftInBatch = Math.ceil(visiblePhotoCount / cols) - (row + 1);
        if (
          rowsLeftInBatch <= MOUNT_AHEAD_ROWS &&
          visiblePhotoCount < photoPaths.length &&
          endIdx >= mountExpandedAtRef.current
        ) {
          mountExpandedAtRef.current = visiblePhotoCount + PHOTO_MOUNT_BATCH;
          expandVisiblePhotos();
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [photoPaths, visiblePhotoCount, columnCount, getPhotoSrc, expandVisiblePhotos]);

  useEffect(() => {
    loadedThroughRef.current = -1;
    mountExpandedAtRef.current = 0;
    setLoadThroughIndex(-1);
  }, [columnCount]);

  /** Триггер на карточке за несколько строк до конца — монтируем следующую порцию заранее */
  useEffect(() => {
    const sentinel = prefetchSentinelRef.current;
    if (!sentinel || visiblePhotoCount >= photoPaths.length) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) expandVisiblePhotos();
      },
      { rootMargin: PREFETCH_ROOT_MARGIN },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [photoPaths.length, visiblePhotoCount, columnCount, expandVisiblePhotos]);

  useEffect(() => {
    const el = masonryContainerRef.current;
    if (!el) return;
    const update = () => setMasonryWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [photoPaths.length, visiblePhotoCount]);

  const getEstimatedHeight = useCallback(
    (path: string) => {
      const dim = photoDimensionsRef.current[path];
      const colWidth = masonryWidth > 0 ? masonryWidth / columnCount : 280;
      if (!dim?.width) return 220;
      return Math.max(72, Math.round(colWidth * (dim.height / dim.width)));
    },
    [columnCount, masonryWidth],
  );

  useEffect(() => {
    if (lightboxIndex === null) return;
    if (lightboxIndex >= visiblePhotoCount - 2 && visiblePhotoCount < photoPaths.length) {
      expandVisiblePhotos();
    }
  }, [lightboxIndex, visiblePhotoCount, photoPaths.length, expandVisiblePhotos]);

  const downloadPhoto = async (path: string) => {
    if (!path) return;
    try {
      const { url } = await galleryAPI.getDownloadUrl(path);
      const link = document.createElement('a');
      link.href = url;
      link.download = path.split('/').pop() || 'wedding-photo.jpg';
      link.click();
    } catch {
      setMessage('Не удалось получить ссылку на скачивание');
    }
  };

  const downloadPhotoArchive = () => {
    if (photoArchiveUrl) {
      const link = document.createElement('a');
      link.href = photoArchiveUrl;
      link.download = 'wedding-photos-archive.zip';
      link.click();
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-cream)' }}>
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {message && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 text-red-700 text-center">
            {message}
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif gradient-text">
            Галерея
          </h1>
        </motion.div>

        {videoEnabled === null || photosEnabled === null ? (
          /* Пока не знаем статус — минимальный лоадер, без скелетона */
          <div className="flex justify-center items-center py-16">
            <Loader2 className="w-10 h-10 animate-spin" style={{ color: 'var(--color-lilac)' }} />
          </div>
        ) : galleryClosed ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto"
          >
            <div className="elegant-card elegant-card-no-hover p-8 md:p-12 text-center">
              <div className="flex justify-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: 'var(--gradient-main)' }}>
                  <Camera className="w-7 h-7 text-white" />
                </div>
                <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: 'var(--gradient-main)' }}>
                  <Video className="w-7 h-7 text-white" />
                </div>
              </div>
              <h2 className="text-xl md:text-2xl font-serif font-semibold mb-4 gradient-text">
                Скоро здесь будет галерея
              </h2>
              <p className="text-base md:text-lg leading-relaxed" style={{ color: 'var(--color-text-light)' }}>
                Все видео и фотографии со свадьбы будут доступны в скором времени после окончания мероприятия.
              </p>
              <p className="mt-4 text-sm" style={{ color: 'var(--color-text-lighter)' }}>
                Следите за обновлениями — мы обязательно поделимся лучшими моментами нашего дня.
              </p>
            </div>
          </motion.div>
        ) : loading ? (
          <>
            {videoEnabled && (
              <>
                <div className="mb-16">
                  <div className="elegant-card elegant-card-no-hover p-6 md:p-8">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 rounded-full animate-pulse" style={{ background: 'var(--color-cream-light)' }} />
                      <div className="h-8 w-48 rounded-lg animate-pulse" style={{ background: 'var(--color-cream-light)' }} />
                    </div>
                    <div className="rounded-2xl overflow-hidden aspect-video flex items-center justify-center animate-pulse" style={{ background: 'var(--color-cream-light)', minHeight: 280 }}>
                      <Loader2 className="w-12 h-12 animate-spin opacity-50" style={{ color: 'var(--color-lilac)' }} />
                    </div>
                    <div className="flex gap-3 mt-4">
                      <div className="flex-1 h-12 rounded-xl animate-pulse" style={{ background: 'var(--color-cream-light)' }} />
                      <div className="flex-1 h-12 rounded-lg animate-pulse" style={{ background: 'var(--color-cream-light)' }} />
                    </div>
                  </div>
                </div>
                <div className="mb-16">
                  <div className="elegant-card elegant-card-no-hover p-6 md:p-8">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 rounded-full animate-pulse" style={{ background: 'var(--color-cream-light)' }} />
                      <div className="h-8 w-56 rounded-lg animate-pulse" style={{ background: 'var(--color-cream-light)' }} />
                    </div>
                    <div className="rounded-2xl overflow-hidden aspect-video flex items-center justify-center animate-pulse" style={{ background: 'var(--color-cream-light)', minHeight: 280 }}>
                      <Loader2 className="w-12 h-12 animate-spin opacity-50" style={{ color: 'var(--color-lilac)' }} />
                    </div>
                    <div className="flex gap-3 mt-4">
                      <div className="flex-1 h-12 rounded-xl animate-pulse" style={{ background: 'var(--color-cream-light)' }} />
                      <div className="flex-1 h-12 rounded-lg animate-pulse" style={{ background: 'var(--color-cream-light)' }} />
                    </div>
                  </div>
                </div>
              </>
            )}
            {photosEnabled && (
            <div className="mb-8">
              <div className="flex items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full animate-pulse" style={{ background: 'var(--color-cream-light)' }} />
                  <div className="h-8 w-40 rounded-lg animate-pulse" style={{ background: 'var(--color-cream-light)' }} />
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="aspect-[4/3] rounded-2xl animate-pulse" style={{ background: 'var(--color-cream-light)' }} />
                ))}
              </div>
            </div>
            )}
          </>
        ) : (
          <>
        {videoEnabled && (
          <>
        {/* Трейлер — первым блоком */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-16"
        >
          <GalleryVideoBlock
            title="Трейлер"
            streamUrl={bestMomentsStreamUrl}
            downloadUrl={bestMomentsDownloadUrl}
            archiveUrl={bestMomentsArchiveUrl}
            downloadFileName="wedding_best_moments.mp4"
            archiveDownloadFileName="wedding_best_moments.zip"
            archiveLabel="Скачать архив с трейлером"
            previewTimeSec={5}
          />
        </motion.section>

        {/* Видео со свадьбы */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-16"
        >
          <GalleryVideoBlock
            title="Видео со свадьбы"
            streamUrl={videoStreamUrl}
            downloadUrl={videoDownloadUrl}
            archiveUrl={videoArchiveUrl}
            downloadFileName="wedding_video.mp4"
            archiveDownloadFileName="wedding-video-archive.zip"
            archiveLabel="Скачать архив с видео"
          />
        </motion.section>
          </>
        )}

        {photosEnabled && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-3">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ background: 'var(--gradient-main)' }}
                >
                  <ImageIcon className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl md:text-3xl font-serif font-semibold" style={{ color: 'var(--color-text)' }}>
                  Фотографии
                </h2>
              </div>
              {photoArchiveUrl && (
                <button
                  onClick={downloadPhotoArchive}
                  className="flex items-center gap-2 px-6 py-3 rounded-xl text-white font-semibold transition-all hover:shadow-lg"
                  style={{ background: 'linear-gradient(135deg, #d4af37, #f4e4a6)' }}
                >
                  <Download className="w-5 h-5" />
                  <span>Скачать архив ({photoPaths.length} фото)</span>
                </button>
              )}
            </div>

            {photoPaths.length > 0 ? (
              <>
                {photosUrlsLoading && (
                  <p className="text-center text-sm mb-4" style={{ color: 'var(--color-text-lighter)' }}>
                    Загружаем фотографии…
                  </p>
                )}
                <div ref={masonryContainerRef}>
                <GalleryMasonry
                  columnCount={columnCount}
                  gutter={MASONRY_GUTTER}
                  paths={visiblePaths}
                  layoutEpoch={layoutEpoch}
                  getEstimatedHeight={getEstimatedHeight}
                  renderItem={(path, index, reportHeight) => {
                    const prefetchTriggerIndex = Math.max(
                      0,
                      visiblePhotoCount - columnCount * MOUNT_AHEAD_ROWS - 1,
                    );
                    const isPrefetchTrigger =
                      visiblePhotoCount < photoPaths.length && index === prefetchTriggerIndex;

                    return (
                      <GalleryPhotoCard
                        thumbSrc={getPhotoSrc(path)}
                        alt={`Фото ${index + 1}`}
                        canLoad={index <= loadThroughIndex}
                        rootRef={isPrefetchTrigger ? prefetchSentinelRef : undefined}
                        onDownload={() => downloadPhoto(path)}
                        onOpen={() => setLightboxIndex(index)}
                        onPrefetchFull={() => {
                          const url = photoUrlByPath[path];
                          if (url) {
                            const img = new Image();
                            img.src = url;
                          }
                        }}
                        onImageLoad={(height) => reportHeight(path, height)}
                      />
                    );
                  }}
                />
                </div>
              </>
            ) : photosUrlsLoading ? (
              <div
                className="grid gap-2"
                style={{ gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))` }}
              >
                {Array.from({ length: 12 }).map((_, i) => (
                  <div
                    key={i}
                    className="rounded-lg md:rounded-2xl min-h-[72px] md:min-h-[200px] animate-pulse"
                    style={{ background: 'linear-gradient(135deg, rgba(184, 162, 200, 0.12), rgba(144, 198, 149, 0.08))' }}
                  />
                ))}
              </div>
            ) : (
              <p className="text-center py-8" style={{ color: 'var(--color-text-light)' }}>
                Фотографии пока нет в галерее
              </p>
            )}
          </div>
        </motion.section>
        )}
          </>
        )}
      </div>

      {lightboxIndex !== null && visiblePaths.length > 0 && (
        <GalleryPhotoLightbox
          paths={visiblePaths}
          currentIndex={lightboxIndex}
          getThumbSrc={getPhotoSrc}
          getFullSrc={(p) => photoUrlByPath[p]}
          getPhotoDimensions={(p) => photoDimensionsRef.current[p]}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
          onDownload={downloadPhoto}
        />
      )}
    </div>
  );
};
